<style lang="scss" scoped>
.admin-dashboard {
	h2 {
		margin-top: 20px;
		margin-bottom: 40px;

		&:first-child {
			margin-top: 0;
		}
	}
}

.banners {
	@include flex-center;
}
</style>

<template lang="pug">
.admin-dashboard
	introduction-dialog(v-if="user && !user.seen_admin_wizard_at && user.School.shard_id === 2 && !user.originally_organisation_admin")
	scholar-pack-create-token-notice
	install-notice(v-if="readyAndAdmin")
	upgrade-notice(v-if="readyWithCompanies")
	loader(v-if='$apollo.loading', :message="trans('page_dashboard.m_loading')", :visible='true')
	template(v-else)
		//- welcome-message(v-if="readyWithCompanies")
		empty(message="You have no apps on your account", :central="true" :visible="_apps.length === 0")
		organisation-pending-notice(v-if="user.is_admin")
		.banners
			data-recovery-banner(v-if="promotionalApplications.dataRecovery", size="medium")
			department-for-education-banner(v-if="promotionalApplications.departmentForEducation", size="medium")
			free-school-meals-banner(v-if="promotionalApplications.evouchers", :size="promotionalApplications.evouchersSize")
			holiday-activities-banner(v-if="promotionalApplications.holidayActivities", :size="promotionalApplications.holidayActivitySize")
		//- h2 Single Sign-On applications
			tooltip-icon You installed these applications from the Discover marketplace
		el-row(:gutter="50")
			el-col(v-for='app in apps', :key="app.id", :span="4")
				admin-tile(:app="app", :user="user")
			el-col(v-if="user.School.shard_id !== 2" :span="4")
				add-new-app-tile(@click="discoverApps") Discover Apps
		template(v-if="user.School.sso_enabled")
			h2 Custom dashboard tiles
				tooltip-icon These are custom links that you can point to any resource / website.
			el-row(:gutter="50")
				el-col(v-for='app in tileApps', :key="app.id", :span="4")
					admin-tile(:app="app", :user="user")
				el-col(:span="4")
					add-new-app-tile(@click="addNewTile") Create tile
</template>

<script>
import gql from "graphql-tag";
import ScholarPackCreateTokenNotice from "@/components/apps/appsList/cards/admin/ScholarPackCreateTokenNotice";
import OrganisationPendingNotice from "@/components/apps/appsList/cards/admin/OrganisationPendingNotice";
import UpgradeNotice from "@/components/apps/appsList/cards/admin/UpgradeNotice";
import InstallNotice from "@/components/apps/appsList/cards/admin/InstallNotice";
import WelcomeMessage from "@/components/apps/appsList/cards/admin/WelcomeMessage";
import FreeSchoolMealsBanner from "@/components/apps/promotions/freeSchoolMeals/HomepageBanner";
import HolidayActivitiesBanner from "@/components/apps/promotions/holidayActivities/HomepageBanner";
import DepartmentForEducationBanner from "@/components/apps/promotions/departmentForEducation/HomepageBanner";
import DataRecoveryBanner from "@/components/apps/promotions/dataRecovery/HomepageBanner";
import AdminTile from "@/components/apps/appsList/cards/admin/Tile";
import AddNewAppTile from "@/components/private/components/pages/class/includes/AddNewAppTile";
import TooltipIcon from "@/components/ui/TooltipIcon";
import loadAllApplicationTypes from "@/components/mixins/loadAllApplicationTypes.mixin";
import IntroductionDialog from "@/components/apps/appsList/cards/admin/modals/Dialog.vue";
import WelshDHA from "@/components/apps/appsList/cards/admin/modals/WelshDHA.vue";

export default {
	components: {
		ScholarPackCreateTokenNotice,
		OrganisationPendingNotice,
		UpgradeNotice,
		WelcomeMessage,
		InstallNotice,
		AdminTile,
		AddNewAppTile,
		TooltipIcon,
		FreeSchoolMealsBanner,
		HolidayActivitiesBanner,
		DepartmentForEducationBanner,
		DataRecoveryBanner,
		IntroductionDialog,
	},
	mixins: [loadAllApplicationTypes],
	data() {
		return {
			loading: true,
			showPassword: false,
			companies: null,
			user: null,
			randomNumber: Math.floor(Math.random() * 11),
		};
	},
	computed: {
		promotionalApplications() {
			let showDepartmentForEducation = this.showDepartmentForEducation;
			let showDataRecovery = this.showDataRecovery;

			if (showDepartmentForEducation && showDataRecovery) {
				const weightToDataRecovery = 7 >= this.randomNumber;

				showDepartmentForEducation = !weightToDataRecovery;
				showDataRecovery = weightToDataRecovery;
			}

			const promotionalApplications = {
				departmentForEducation: showDepartmentForEducation,
				dataRecovery: showDataRecovery,
				holidayActivities: this.showHolidayActivities,
				evouchers: this.showEvouchers,
			};

			const count = Object.keys(promotionalApplications).reduce((count, key) => count + promotionalApplications[key], 0);

			return {
				...promotionalApplications,
				count,
				evouchersSize: count === 1 ? "large" : count === 2 ? "medium" : "small",
				holidayActivitySize: count === 1 ? "large" : count === 2 ? "medium" : "small",
			};
		},
		showDepartmentForEducation() {
			if (!this.DepartmentForEducation || !this.user) {
				return false;
			}

			const appIsApproved = this.DepartmentForEducation.State && this.DepartmentForEducation.State.approved;

			return !appIsApproved && this.user.School.isEngland;
		},
		showDataRecovery() {
			if (!this.DataRecovery || !this.user) {
				return false;
			}

			const appIsApproved = this.DataRecovery.State && this.DataRecovery.State.approved;

			return !appIsApproved && this.user.School.isEngland;
		},
		showHolidayActivities() {
			if (!this.HolidayActivities || !this.user) {
				return false;
			}

			const appIsApproved = this.HolidayActivities.State && this.HolidayActivities.State.approved;

			return appIsApproved;
		},
		showEvouchers() {
			if (!this.user) {
				return false;
			}

			return this.user.School.shard_id === 1;
		},
		apps() {
			return this._filterApps(["sync", "sso", "company_association"]);
		},
		tileApps() {
			return this._filterApps("tile").filter((tile) => !tile.classroom_uuid);
		},
		readyAndAdmin() {
			return !this.$apollo.loading && this.user.is_admin;
		},
		readyAdminWithSSO() {
			return !this.$apollo.loading && this.user.is_admin && this.user.School.sso_enabled;
		},
		readyWithCompanies() {
			return !this.$apollo.loading && this._apps && this._apps.length > 0;
		},
	},
	apollo: {
		Evouchers: {
			query: gql`
				query getTestRegisterCompany($id: String!) {
					CompanyById_v2(id: $id) {
						id
						redirect
						client_id
						State {
							approved
							declined
							audit_approved_at
						}
					}
				}
			`,
			update: (d) => d.CompanyById_v2,
			variables() {
				return {
					id: process.env.FREE_SCHOOL_MEALS_WONDE_ID,
				};
			},
		},
		HolidayActivities: {
			query: gql`
				query getTestRegisterCompany($id: String!) {
					CompanyById_v2(id: $id) {
						id
						redirect
						client_id
						State {
							approved
							declined
							audit_approved_at
						}
					}
				}
			`,
			update: (d) => d.CompanyById_v2,
			variables() {
				return {
					id: process.env.HOLIDAY_ACTIVITIES_ID,
				};
			},
		},
		DepartmentForEducation: {
			query: gql`
				query getTestRegisterCompany($id: String!) {
					CompanyById_v2(id: $id) {
						id
						redirect
						client_id
						State {
							approved
							declined
							audit_approved_at
						}
					}
				}
			`,
			update: (d) => d.CompanyById_v2,
			variables() {
				return {
					id: process.env.DEPARTMENT_FOR_EDUCATION_ID,
				};
			},
		},
		DataRecovery: {
			query: gql`
				query getTestRegisterCompany($id: String!) {
					CompanyById_v2(id: $id) {
						id
						redirect
						client_id
						State {
							approved
							declined
							audit_approved_at
						}
					}
				}
			`,
			update: (d) => d.CompanyById_v2,
			variables() {
				return {
					id: process.env.DATA_RECOVERY_ID,
				};
			},
		},
		user: {
			query: gql`
				{
					User {
						id
						is_admin
						originally_organisation_admin
						seen_admin_wizard_at
						School {
							id
							sso_enabled
							shard_id
							isEngland
							show_welsh_dha
							welsh_dha_agreed_at
						}
					}
				}
			`,
			update: (r) => r.User,
			result() {
				if (!localStorage.getItem(`${this.user.id}-hide_welsh_dha`) && this.user.School.show_welsh_dha && !this.user.School.welsh_dha_agreed_at) {
					this.$vuedals.open({
						component: WelshDHA,
						title: "",
						props: {
							userId: this.user.id,
						},
						onClose: async (response) => {
							if (response) {
								localStorage.removeItem(`${this.user.id}-hide_welsh_dha`);

								await this.$apollo.mutate({
									mutation: gql`
										mutation UpdateSchool($agreedWelshDHA: Boolean) {
											UpdateSchool(agreedWelshDHA: $agreedWelshDHA) {
												id
												show_welsh_dha
												welsh_dha_agreed_at
											}
										}
									`,
									variables: {
										agreedWelshDHA: true,
									},
								});
							}
						},
					});
				}
			},
		},
	},
	methods: {
		discoverApps() {
			window.open(process.env.DISCOVER_URL, "_self");
		},
		addNewTile() {
			this.$router.push({ name: "settings.newTile" });
		},
	},
};
</script>



// WEBPACK FOOTER //
// src/components/apps/appsList/cards/admin/Homepage.vue